/**
 * /signup — self-service account request.
 *
 * On 201 the account exists with status=pending and CANNOT log in
 * until an admin approves it. We show an "awaiting approval" success
 * card rather than auto-logging in.
 */

import { CheckCircleOutline } from '@mui/icons-material';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { signupRequest } from '../../api/auth';
import { AuthLayout } from './AuthLayout';
import { AuthPrimaryButton } from './AuthPrimaryButton';
import { AuthTextField } from './AuthTextField';
import { describeError } from './errorMessages';

export function SignupPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signupRequest({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      setSubmitted(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Request received"
        footer={
          <>
            Already approved? <Link to="/login">Sign in</Link>
          </>
        }
      >
        <Stack spacing={2.5} alignItems="center" sx={{ py: 1 }}>
          <CheckCircleOutline sx={{ fontSize: 56, color: 'success.main' }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1">Your account request has been submitted.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              An administrator will review your request shortly. You'll receive an email at{' '}
              <strong>{email}</strong> once your account is approved.
            </Typography>
          </Box>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Request an account"
      subtitle="An administrator will review your request before granting access."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <AuthTextField
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoComplete="given-name"
              autoFocus
            />
            <AuthTextField
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              autoComplete="family-name"
            />
          </Stack>

          <AuthTextField
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <AuthTextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            helperText="At least 8 characters."
          />

          <AuthPrimaryButton
            type="submit"
            disabled={!firstName || !lastName || !email || password.length < 8}
            loading={submitting}
          >
            Request account
          </AuthPrimaryButton>
        </Stack>
      </form>
    </AuthLayout>
  );
}
